// Chairman Service for API communication
interface ChairmanData {
  id: number;
  type: string;
  title: string;
  content: string;
  icon: string | null;
  link: string | null;
  ceo_name: string; // Reusing ceo_name field for chairman_name
  ceo_position: string; // Reusing ceo_position field for chairman_position
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ChairmanResponse {
  success: boolean;
  data: ChairmanData | null;
  message?: string;
}

interface ChairmanUpdateResponse {
  success: boolean;
  message: string;
  data: ChairmanData;
}

class ChairmanService {
  private baseUrl: string;
  private lastRequestTime: number = 0;
  private minRequestInterval: number = 1000; // Minimum 1 second between requests

  constructor() {
    // Use mock API for development, production API when database is connected
    // With proxy configuration, we can use relative URLs
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
    console.log('Chairman Service initialized with baseUrl:', this.baseUrl);
  }

  /**
   * Get current Chairman data with rate limiting protection
   */
  async getChairman(): Promise<ChairmanResponse> {
    try {
      // Check rate limiting
      const now = Date.now();
      if (now - this.lastRequestTime < this.minRequestInterval) {
        console.log('Chairman request throttled to prevent rate limiting');
        // Return cached data or wait
        await new Promise(resolve => setTimeout(resolve, this.minRequestInterval - (now - this.lastRequestTime)));
      }
      
      this.lastRequestTime = Date.now();
      const response = await fetch(`${this.baseUrl}/static-data/chairman`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching Chairman data:', error);
      return {
        success: false,
        data: null,
        message: 'Failed to fetch Chairman data'
      };
    }
  }

  /**
   * Update Chairman data with optional image upload
   * Creates new Chairman if none exists
   */
  async updateChairman(
    title: string,
    content: string,
    chairmanName: string,
    chairmanPosition: string,
    imageFile?: File
  ): Promise<ChairmanUpdateResponse> {
    try {
      const formData = new FormData();
      
      formData.append('title', title);
      formData.append('content', content);
      formData.append('chairman_name', chairmanName);
      formData.append('chairman_position', chairmanPosition);
      
      if (imageFile) {
        formData.append('chairman_image', imageFile);
      }

      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${this.baseUrl}/static-data/chairman`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData // Don't set Content-Type header, let browser set it for FormData
      });

      const data = await response.json();
      
      // Log the response for debugging
      console.log('Chairman API Response:', data);
      
      return data;
    } catch (error) {
      console.error('Error updating Chairman data:', error);
      return {
        success: false,
        message: 'Failed to update Chairman data',
        data: {} as ChairmanData
      };
    }
  }

  /**
   * Get image URL for Chairman photo
   */
  getImageUrl(iconPath: string | null): string {
    if (!iconPath) {
      return '/assets/images/md-ceo.svg'; // Default fallback image (same as CEO)
    }
    
    // If it's already a full URL, return as is
    if (iconPath.startsWith('http')) {
      return iconPath;
    }
    
    // If it's a relative path starting with /uploads, return as relative URL (proxy will handle it)
    if (iconPath.startsWith('/uploads')) {
      return iconPath; // Return relative path, proxy will handle routing to backend
    }
    
    // Otherwise assume it's an asset path
    return iconPath;
  }
}

// Export singleton instance
const chairmanService = new ChairmanService();
export default chairmanService;

// Export types for use in components
export type { ChairmanData, ChairmanResponse, ChairmanUpdateResponse };
